/**
 * components/Header.jsx
 * Page title, subtitle, "Add New" CTA, and "Generate with AI" button.
 */

import { useState } from "@wordpress/element";
import { createPortal } from "@wordpress/element";
import { __ } from "@wordpress/i18n";
import { Plus, Lock, Sparkles, Zap, X } from "lucide-react";
import { getAddNewUrl, useCrudListState } from "../context/CrudContext";
import { createSettings } from "@/admin/pages/rulesDetails/api/settings";
import AiGeneratorModal from "@/admin/pages/rulesDetails/components/AiGeneratorModal";

function UpgradeModal({ open, onClose, upgradeUrl }) {
    if (!open) return null;
    const portal = document.getElementById("wpbean-pgs-settings-app");

    const modal = (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.45)" }}
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg shadow-xl w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
            >
                <div className="flex items-start justify-between p-5 pb-4">
                    <div className="flex items-center gap-3">
                        <span className="flex items-center justify-center w-9 h-9 rounded-full bg-amber-100 shrink-0">
                            <Zap className="w-4.5 h-4.5 text-amber-500" />
                        </span>
                        <h3 className="text-base font-semibold text-gray-900 m-0">
                            {__("Unlock Unlimited Rules", "wpbean-product-gallery-slider-for-woocommerce")}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-0.5 rounded"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="px-5 pb-5">
                    <p className="text-sm text-gray-600 mb-2 mt-0">
                        {__("You're on the free plan, which includes 1 gallery rule.", "wpbean-product-gallery-slider-for-woocommerce")}
                    </p>
                    <p className="text-sm text-gray-600 mb-5 mt-0">
                        {__("Upgrade to Pro to create unlimited rules — assign different gallery layouts to different products, categories, or your entire store.", "wpbean-product-gallery-slider-for-woocommerce")}
                    </p>
                    <div className="flex justify-end gap-2.5">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                            {__("Not Now", "wpbean-product-gallery-slider-for-woocommerce")}
                        </button>
                        <a
                            href={upgradeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 rounded-md no-underline transition-colors"
                        >
                            <Zap className="w-3.5 h-3.5" />
                            {__("Upgrade to Pro", "wpbean-product-gallery-slider-for-woocommerce")}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );

    return portal ? createPortal(modal, portal) : modal;
}

export default function Header() {
    const { limitReached, loaded } = useCrudListState();
    const isPremium  = window.WPBean_PGS_Options?.is_premium;
    const upgradeUrl = window.WPBean_PGS_Options?.upgradeUrl || "#";
    const adminUrl   = window.WPBean_PGS_Options?.adminUrl || "";
    const isLocked   = !isPremium && limitReached;

    const [aiOpen, setAiOpen]         = useState(false);
    const [creating, setCreating]     = useState(false);
    const [upgradeOpen, setUpgradeOpen] = useState(false);

    const handleAiApply = async (settings, title) => {
        setCreating(true);
        try {
            const { id } = await createSettings({
                title:  title || __("AI Gallery Rule", "wpbean-product-gallery-slider-for-woocommerce"),
                status: "draft",
                config: settings,
            });
            const url = new URL(adminUrl + "admin.php");
            url.searchParams.set("page", "wpbean-product-gallery-rule");
            url.searchParams.set("id", id);
            window.location.href = url.toString();
        } catch {
            setCreating(false);
        }
    };

    return (
        <>
            <div className="px-4 sm:px-6 py-5 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 p-0 m-0">
                            {__("Settings Rules", "wpbean-product-gallery-slider-for-woocommerce")}
                        </h1>
                        <p className="mt-1 text-xs sm:text-sm text-gray-500">
                            {__("Manage your zoom settings rules and configurations.", "wpbean-product-gallery-slider-for-woocommerce")}
                        </p>
                    </div>

                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        {/* Generate with AI */}
                        {loaded && !isLocked && (
                            <button
                                type="button"
                                disabled={creating}
                                onClick={() => setAiOpen(true)}
                                className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-medium border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 hover:border-violet-300 transition-all w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                <Sparkles className="w-3.5 h-3.5" />
                                {__("Generate with AI", "wpbean-product-gallery-slider-for-woocommerce")}
                            </button>
                        )}

                        {/* Add New */}
                        {isLocked ? (
                            <button
                                type="button"
                                onClick={() => setUpgradeOpen(true)}
                                title={__("Upgrade to Pro to create unlimited rules", "wpbean-product-gallery-slider-for-woocommerce")}
                                className="inline-flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-400 border border-gray-200 text-sm font-medium rounded-md w-full sm:w-auto hover:bg-gray-200 transition-colors cursor-pointer"
                            >
                                <Lock className="w-4 h-4 me-2" />
                                {__("Add New", "wpbean-product-gallery-slider-for-woocommerce")}
                            </button>
                        ) : (
                            <a
                                href={getAddNewUrl()}
                                className="inline-flex items-center justify-center px-4 py-2 bg-accent text-white border border-accent shadow-btn-accent hover:bg-accent-hover hover:border-accent-hover text-sm font-medium rounded-md transition-all w-full sm:w-auto no-underline"
                            >
                                <Plus className="w-4 h-4 me-2" />
                                {__("Add New", "wpbean-product-gallery-slider-for-woocommerce")}
                            </a>
                        )}
                    </div>
                </div>
            </div>

            <AiGeneratorModal
                open={aiOpen}
                onClose={() => setAiOpen(false)}
                onApply={handleAiApply}
                mode="create"
            />
            <UpgradeModal
                open={upgradeOpen}
                onClose={() => setUpgradeOpen(false)}
                upgradeUrl={upgradeUrl}
            />
        </>
    );
}
